// Notizfeld zum Anruf oben im Verlauf der Lead-Seite (wie Close: während des Gesprächs parallel eine Notiz schreiben).
// Name, Status und Dauer stehen NICHT hier, sondern in der Anrufleiste oben (einheitlich für jeden Anruf).
// 21.10.2026 (Wunsch Dilsar): Die Karte öffnet sich beim Wählen und bleibt nach dem Auflegen OFFEN und editierbar. Sie klappt erst
// zu, wenn man unter dem Feld auf „Fertig“ klickt oder zum nächsten Lead / auf eine andere Seite wechselt (der Provider speichert
// dann und schließt sie, `notizOffen` / `notizFertig`). Nie zuklappen, während jemand tippt. Danach steht der Anruf als EINE Karte
// im Verlauf unten (Anruf-Aktivität mit Dauer, Ergebnis, Aufnahme und der Notiz darin; Feed.tsx), dort lässt sich die Notiz
// weiter bearbeiten und anheften. Gespeichert wird automatisch (kurz nach dem Tippen, beim Verlassen des Feldes, beim Auflegen,
// bei „Nächster Anruf“, beim Schließen der Seite) oder per „Speichern“/„Fertig“. Nichts geht verloren.
// Einbau: <AnrufKarte leadId={…} /> über dem Feed (pages/Lead.tsx).
// 19.10.2026 (Simon): Sobald gewählt wird, steht die Karte offen da und das Notizfeld hat den Fokus (Tippen geht sofort),
// auch direkt nach „Nächster Anruf“. Den Fokus nimmt es nur, wenn man nicht gerade in einem anderen Feld schreibt.
import { useEffect, useRef } from "react";
import { notizKarteSichtbar } from "./logik";
import { useTelefonie } from "./TelefonieProvider";
import "./telefonie.css";

export function AnrufKarte({ leadId }: { leadId: string }) {
  const t = useTelefonie();
  const sichtbar = notizKarteSichtbar(t.notizOffen, leadId);
  const imGespraech = t.aktiverAnruf?.leadId === leadId;
  const feld = useRef<HTMLTextAreaElement>(null);

  // Beim Wählen (und bei jedem neuen Anruf im Power Dialer) das Notizfeld öffnen und fokussieren
  useEffect(() => {
    if (!imGespraech) return;
    const t0 = window.setTimeout(() => {
      const el = feld.current;
      if (!el || document.activeElement === el) return;
      const jetzt = document.activeElement as HTMLElement | null;
      const schreibtWoanders =
        !!jetzt && jetzt !== document.body && (jetzt.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(jetzt.tagName));
      if (schreibtWoanders) return;
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: "nearest" });
    }, 0);
    return () => window.clearTimeout(t0);
  }, [imGespraech, leadId]);

  // Textfeld wächst mit dem Inhalt
  useEffect(() => {
    const el = feld.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(240, el.scrollHeight)}px`;
  }, [t.notiz, sichtbar]);

  if (!sichtbar) return null;
  const stand = t.notizStand;

  return (
    <section className={`tel-akarte${imGespraech ? " tel-akarte--aktiv" : " tel-akarte--beendet"}`} aria-label="Anruf" data-anrufkarte={imGespraech ? "aktiv" : "beendet"}>
      <div className="tel-akarte__karte">
        <div className="tel-akarte__notiz">
          <textarea
            ref={feld}
            className="tel-akarte__feld"
            rows={3}
            placeholder="Notiz zum Anruf (wird automatisch gespeichert) …"
            aria-label="Notiz zum Anruf"
            value={t.notiz}
            onChange={(e) => t.setNotiz(e.target.value)}
            onBlur={() => void t.notizJetzt()}
          />
        </div>
        <footer className="tel-akarte__fuss">
          <span className="tel-akarte__stand tel-akarte__stand--speichern" data-notizstand={stand} aria-live="polite">
            {stand === "speichert" ? "Speichert …" : stand === "gespeichert" ? "Gespeichert" : stand === "fehler" ? "Nicht gespeichert, neuer Versuch mit „Speichern“" : stand === "offen" ? "Wird gleich gespeichert" : "Wird automatisch gespeichert"}
          </span>
          <button type="button" className="knopf knopf--klein" data-notiz-speichern="" disabled={!t.notiz.trim() || stand === "speichert"} onClick={() => void t.notizJetzt()}>
            Speichern
          </button>
          {imGespraech ? null : (
            <button type="button" className="knopf knopf--klein knopf--primaer" data-notiz-fertig="" onClick={() => void t.notizFertig()}>
              Fertig
            </button>
          )}
        </footer>
      </div>
    </section>
  );
}
